import React from 'react'
import { useCurrentFrame, useVideoConfig, interpolate } from 'remotion'
import { COLORS, FONTS, GLOW } from '../shared/theme'
import {
  fadeIn,
  entrance,
  staggerDelay,
  breathe,
  pulse,
  float,
  SPRINGS,
} from '../shared/animations'
import SceneWrapper from '../shared/SceneWrapper'

/**
 * ConceptIllustration — Concepto abstracto con nodos geometricos SVG
 * y conexiones animadas con energy flow.
 *
 * params: { concept, icons: [str], connections: [{from, to}] }
 */

/* ── Geometric icon shapes ── */
const SHAPES = ['hexagon', 'diamond', 'circle', 'octagon']

function drawShape(type, cx, cy, size) {
  const s = size
  switch (type) {
    case 'hexagon': {
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2
        return `${cx + Math.cos(a) * s},${cy + Math.sin(a) * s}`
      })
      return <polygon points={pts.join(' ')} />
    }
    case 'diamond':
      return (
        <polygon
          points={`${cx},${cy - s} ${cx + s * 0.75},${cy} ${cx},${cy + s} ${cx - s * 0.75},${cy}`}
        />
      )
    case 'octagon': {
      const r = s
      const pts = Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2 - Math.PI / 8
        return `${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`
      })
      return <polygon points={pts.join(' ')} />
    }
    default:
      return <circle cx={cx} cy={cy} r={s} />
  }
}

export default function ConceptIllustration({ params = {}, artDirection = {} }) {
  const frame = useCurrentFrame()
  const { fps, durationInFrames } = useVideoConfig()
  const { concept = '', icons = [], connections = [] } = params

  const titleOpacity = fadeIn(frame, 0, 20)
  const subtitleOpacity = fadeIn(frame, 25, 20)
  const centerBreath = breathe(frame, 0.025, 0.012)
  const glowPulse = pulse(frame, 0.04, 0.4, 1.0)

  const count = Math.min(icons.length, 4)
  const radius = 200
  const cx = 500
  const cy = 310
  const orbitDrift = frame * 0.0015

  const positions = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2 + orbitDrift
    return {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    }
  })

  // Palette per node
  const palette = [
    COLORS.accent.blue,
    COLORS.accent.purple,
    COLORS.accent.teal,
    COLORS.accent.green,
  ]

  return (
    <SceneWrapper accent={COLORS.accent.blue} texture={artDirection?.texture}>
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Title */}
        <div
          style={{
            position: 'absolute',
            top: 70,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity: titleOpacity,
          }}
        >
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.text.dim,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: 12,
            }}
          >
            concepto
          </div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontSize: 52,
              fontWeight: 800,
              color: COLORS.text.primary,
              letterSpacing: '-0.01em',
            }}
          >
            {concept}
          </div>
        </div>

        {/* SVG constellation */}
        <svg
          viewBox="0 0 1000 620"
          style={{ width: '82%', maxWidth: 920, marginTop: 30 }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <filter id="nodeGlow2" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
            </filter>
            <filter id="connGlow2" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
            </filter>
          </defs>

          {/* Connection glow trails */}
          {connections.map((conn, i) => {
            const from = positions[conn.from]
            const to = positions[conn.to]
            if (!from || !to) return null
            const connOpacity = fadeIn(frame, staggerDelay(i, 8) + 20, 15)
            const connPulse = pulse(frame, 0.05 + i * 0.01, 0.2, 0.5)

            // Midpoint for curved connection
            const mx = (from.x + to.x) / 2 + (from.y - to.y) * 0.15
            const my = (from.y + to.y) / 2 + (to.x - from.x) * 0.15
            const pathD = `M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`

            // Energy dot traveling along the path
            const totalT = (frame * 0.008 + i * 0.3) % 1
            const dotX =
              (1 - totalT) * (1 - totalT) * from.x +
              2 * (1 - totalT) * totalT * mx +
              totalT * totalT * to.x
            const dotY =
              (1 - totalT) * (1 - totalT) * from.y +
              2 * (1 - totalT) * totalT * my +
              totalT * totalT * to.y

            return (
              <g key={`conn-${i}`} opacity={connOpacity}>
                {/* Glow path */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={COLORS.accent.blue}
                  strokeWidth={4}
                  filter="url(#connGlow2)"
                  opacity={connPulse * 0.4}
                />
                {/* Main path */}
                <path
                  d={pathD}
                  fill="none"
                  stroke={COLORS.accent.blue}
                  strokeWidth={1.2}
                  strokeDasharray="8 6"
                  strokeDashoffset={-frame * 0.3}
                  opacity={0.4}
                />
                {/* Traveling energy dot */}
                <circle
                  cx={dotX}
                  cy={dotY}
                  r={3}
                  fill={COLORS.accent.teal}
                  opacity={connPulse * 0.8}
                />
                <circle
                  cx={dotX}
                  cy={dotY}
                  r={8}
                  fill={COLORS.accent.teal}
                  filter="url(#connGlow2)"
                  opacity={connPulse * 0.3}
                />
              </g>
            )
          })}

          {/* Center node */}
          <g opacity={fadeIn(frame, 5, 20)}>
            <circle
              cx={cx}
              cy={cy}
              r={62}
              fill={COLORS.accent.blue}
              filter="url(#nodeGlow2)"
              opacity={0.12 * glowPulse}
            />
            <circle
              cx={cx}
              cy={cy}
              r={52 * centerBreath}
              fill="rgba(26,26,26,0.75)"
              stroke={`${COLORS.accent.blue}35`}
              strokeWidth={2}
            />
            <text
              x={cx}
              y={cy + 8}
              textAnchor="middle"
              fontSize={22}
              fill={COLORS.accent.blue}
              fontFamily={FONTS.heading}
              fontWeight={700}
            >
              {concept.length > 10 ? concept.slice(0, 10) : concept}
            </text>
          </g>

          {/* Outer nodes — geometric shapes */}
          {positions.map((pos, i) => {
            const delay = staggerDelay(i, 8) + 8
            const iOpacity = fadeIn(frame, delay, 15)
            const iScale = entrance(frame, fps, delay, SPRINGS.bouncy)
            const iFloat = float(frame, 0.018 + i * 0.006, 5)
            const nodeColor = palette[i % palette.length]
            const shapeType = SHAPES[i % SHAPES.length]

            return (
              <g key={i} opacity={iOpacity} transform={`translate(0, ${iFloat})`}>
                {/* Node glow */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={48 * iScale}
                  fill={nodeColor}
                  filter="url(#nodeGlow2)"
                  opacity={0.12 * glowPulse}
                />
                {/* Geometric shape */}
                <g
                  fill="rgba(26,26,26,0.65)"
                  stroke={`${nodeColor}50`}
                  strokeWidth={2}
                  transform={`translate(${pos.x}, ${pos.y}) scale(${iScale}) translate(${-pos.x}, ${-pos.y})`}
                >
                  {drawShape(shapeType, pos.x, pos.y, 44)}
                </g>
                {/* Icon text (emoji or letter) */}
                <text x={pos.x} y={pos.y + 14} textAnchor="middle" fontSize={34 * iScale}>
                  {icons[i]}
                </text>
                {/* Node label ring */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={52 * iScale}
                  fill="none"
                  stroke={nodeColor}
                  strokeWidth={1}
                  strokeDasharray="4 8"
                  strokeDashoffset={frame * 0.2 * (i % 2 === 0 ? 1 : -1)}
                  opacity={0.25}
                />
              </g>
            )
          })}
        </svg>

        {/* Bottom subtitle */}
        <div
          style={{
            opacity: subtitleOpacity,
            position: 'absolute',
            bottom: 70,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontFamily: FONTS.body,
            fontSize: 20,
            color: COLORS.text.muted,
            letterSpacing: '0.05em',
          }}
        >
          {icons.join('   ')}
        </div>
      </div>
    </SceneWrapper>
  )
}
